import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Box } from "@mui/material";
import CustomTooltip from "./CustomTooltip";

export const makeLabels = (startDate: any, endDate: any) => {
  const newLabels: string[] = [];
  const newDisplayLabels: string[] = [];

  for (
    let time = new Date(startDate);
    time <= endDate;
    time.setMinutes(time.getMinutes() + 30)
  ) {
    const days = time.getDate().toString().padStart(2, "0");
    const month = (time.getMonth() + 1).toString().padStart(2, "0");
    const hours = time.getHours().toString().padStart(2, "0");
    const minutes = time.getMinutes().toString().padStart(2, "0");

    const fullLabel = `${days}/${month}-${hours}:${minutes}`;
    const displayLabel = `${hours}:${minutes}`;

    newLabels.push(fullLabel);
    newDisplayLabels.push(displayLabel);
  }

  return { newLabels, newDisplayLabels };
};

// Регистрируем Chart.js плагины
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const verticalLinePlugin = {
  id: "verticalLine",
  afterDraw: (chart: any) => {
    const ctx = chart.ctx;

    if (
      chart.tooltip &&
      chart.tooltip._active &&
      chart.tooltip._active.length
    ) {
      const activePoint = chart.tooltip._active[0];
      const x = activePoint.element.x;
      const topY = chart.scales.y.top;
      const bottomY = chart.scales.y.bottom;

      ctx.save();

      ctx.beginPath();
      ctx.moveTo(x, topY);
      ctx.lineTo(x, bottomY);

      ctx.setLineDash([5, 5]);
      ctx.lineWidth = 2;
      ctx.strokeStyle = "grey";

      ctx.stroke();

      ctx.restore();
    }
  },
};

type Props = {
  stats: {
    main: number[];
    success?: number[];
    allrequest?: number[];
  };
  selectedDate?: any;
  isMobile?: boolean;
  chartType?: string;
};

const MyChart = ({
  stats = { main: [], success: [] },
  isMobile = false,
  selectedDate,
  chartType = "по общему вводу",
}: Props) => {
  const [labels, setLabels] = useState<string[]>([]);
  const [displayLabels, setDisplayLabels] = useState<string[]>([]);

  const [tooltip, setTooltip] = useState<any>(null);
  const pointerRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const prevTooltipRef = useRef<any>(null);
  const tooltipRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const generateLabels = useCallback((selectedDate: any) => {
    let startDate, endDate;
    if (selectedDate) {
      startDate = new Date(selectedDate);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(startDate);
      endDate.setHours(24, 0, 0, 0);
    } else {
      const currentDate = new Date();
      const minutes = currentDate.getMinutes();
      const roundedMinutes = minutes < 30 ? 0 : 30;

      startDate = new Date(currentDate);
      startDate.setHours(currentDate.getHours() - 24, roundedMinutes, 0, 0);
      endDate = currentDate;
    }
    const { newLabels, newDisplayLabels } = makeLabels(startDate, endDate);
    if (newLabels.length) newLabels.pop();
    if (newDisplayLabels.length) newDisplayLabels.pop();
    setLabels(newLabels);
    setDisplayLabels(newDisplayLabels);
  }, []);

  useEffect(() => {
    generateLabels(selectedDate);
  }, [generateLabels, selectedDate]);

  const mainData = stats.main ?? [];
  const successData = stats.success ?? [];

  const maxValue = useMemo(() => {
    if (!mainData || mainData.length === 0) return 0;
    return Math.max(
      ...mainData.map((v) => (typeof v === "number" && isFinite(v) ? v : 0))
    );
  }, [mainData]);
  const data = useMemo(() => {
    return {
      labels: displayLabels,
      datasets: [
        {
          label:
            chartType === "по количеству заявок" ? "Кол-во заявок" : "Ввод",
          data: mainData,
          borderColor: "rgb(111, 28, 206)",
          backgroundColor: "rgba(101, 27, 185, 0.38)",
          borderWidth: 2,
          fill: true,
          tension: 0.2,
          pointRadius: 4,
          pointBackgroundColor: "rgb(111, 24, 211)",
        },
      ],
    };
  }, [displayLabels, mainData, chartType]);
  const orderData = useMemo(() => {
    return {
      labels: displayLabels,
      datasets: [
        {
          label: "Всего",
          data: mainData,
          borderColor: "#0789ca",
          backgroundColor: "rgba(0,168,255,0.22)",
          borderWidth: 2,
          fill: true,
          tension: 0.2,
          pointRadius: 4,
          pointBackgroundColor: "#0c80b9",
          order: 2,
        },

        ...(successData.length
          ? [
              {
                label: "Успешные",
                data: successData,
                borderColor: "#26a75b",
                backgroundColor: "rgba(46, 204, 112, 0.33)",
                borderWidth: 2,
                fill: true,
                tension: 0.2,
                pointRadius: 3,
                pointBackgroundColor: "rgb(38, 167, 92)",
                order: 1,
              },
            ]
          : []),
      ],
    };
  }, [displayLabels, mainData, successData, chartType]);
  const options = useMemo(() => {
    const suggestedMax = maxValue > 0 ? maxValue * 1.1 : undefined;

    return {
      responsive: true,
      hover: {
        mode: "index" as const,
        intersect: false,
      },
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: false,
          external: (context: any) => {
            const tooltipModel = context.tooltip;
            if (!tooltipModel || tooltipModel.opacity === 0) {
              if (prevTooltipRef.current !== null) {
                prevTooltipRef.current = null;
                setTooltip(null);
              }
              return;
            }
            const points = tooltipModel.dataPoints;
            if (!points || points.length === 0) return;
            const activeData =
              chartType === "запросы" || chartType === "сумма"
                ? orderData
                : data;
            const index = points[0].dataIndex;
            const label = activeData?.datasets?.[0]?.label ?? "";
            const value = activeData?.datasets?.[0]?.data?.[index] ?? "";

            const bodyStr = `${label}: ${value}`;

            const newTooltip = {
              title: labels?.[index] ?? "",
              body: bodyStr,
              allrequest: stats.allrequest
                ? `Кол-во заявок: ${stats.allrequest[index]}`
                : null,
              success: `Успешных: ${successData[index]}`,
              caretX: (pointerRef.current.x ?? 0) - 150,
              caretY: (pointerRef.current.y ?? 0) - 150,
              index,
            };
            const prev = prevTooltipRef.current;
            const same =
              prev &&
              prev.title === newTooltip.title &&
              prev.body === newTooltip.body &&
              prev.caretX === newTooltip.caretX &&
              prev.caretY === newTooltip.caretY &&
              prev.index === newTooltip.index;

            if (!same) {
              prevTooltipRef.current = newTooltip;
              setTooltip(newTooltip);
            }
          },
          mode: "index" as const,
          intersect: false,
        },
      },

      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            font: {
              size: isMobile ? 13 : 15,
            },
          },
        },
        y: {
          min: 0,
          suggestedMax,
          grid: {
            display: true,
            lineWidth: 0.5,
            borderDash: [2, 2],
            drawTicks: false,
            drawOnChartArea: true,
          },
          ticks: {
            callback: function (value: any) {
              if (value >= 1_000_000) {
                return (
                  (value / 1_000_000).toFixed(1).replace(/\.0$/, "") + " кк"
                );
              } else if (value >= 1_000) {
                return (value / 1_000).toFixed(1).replace(/\.0$/, "") + " к";
              }
              return Number(value).toLocaleString("ru-RU");
            },
            font: {
              size: 15,
            },
            padding: isMobile ? 6 : 18,
          },
        },
      },
      onHover: (event: any, chartElement: any) => {
        const e = event?.native;

        if (!e) return;

        // PC
        if (e.offsetX != null && e.offsetY != null) {
          pointerRef.current = {
            x: e.offsetX,
            y: e.offsetY,
          };
        }

        // MOBILE
        if (e.touches && e.touches.length) {
          const touch = e.touches[0];
          const rect = e.target.getBoundingClientRect();

          pointerRef.current = {
            x: touch.clientX - rect.left,
            y: touch.clientY - rect.top,
          };
        }

        try {
          const target = e.target;
          if (target) {
            target.style.cursor =
              chartElement && chartElement.length ? "pointer" : "default";
          }
        } catch {}
      },
    };
  }, [data, labels, isMobile, maxValue]);

  return (
    <Box
      ref={containerRef}
      sx={{
        overflowX: "auto",
        width: "100%",
        position: "relative",
        zIndex: 1,
        scrollbarWidth: "thin",
        scrollbarColor: "grey #1e1e1e",
        msOverflowStyle: "none",
      }}
      className="hide-scrollbar"
    >
      <Box
        sx={{
          minWidth: "1000px",
          position: "relative",
          height: "400px",
          overflow: "hidden",
        }}
      >
        <Line
          data={
            chartType === "запросы" || chartType === "сумма" ? orderData : data
          }
          options={options}
          plugins={[verticalLinePlugin]}
        />
        <CustomTooltip
          containerRef={containerRef}
          ref={tooltipRef}
          tooltip={tooltip}
          chartType={chartType}
        />
      </Box>
    </Box>
  );
};

export default React.memo(MyChart);
